import { PRICING_MODEL } from '@monorepo/shared/schemas/common'
import { useNavigate } from '@tanstack/react-router'
import { Home } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { usePlans } from '@/features/plans/hooks/usePlans'
import { planService } from '@/features/plans/services/planService'
import type { Plan } from '@/features/plans/types/plan.types'
import { PageTitle } from '@/shared/components/common/PageTitle'
import { TaxDialog } from '@/shared/components/taxes/TaxDialog'
import { TaxesSection } from '@/shared/components/taxes/TaxesSection'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/shared/components/ui/breadcrumb'
import { Button } from '@/shared/components/ui/button'
import { useBreadcrumbs } from '@/shared/hooks/useBreadcrumbs'
import { extractErrorMessage } from '@/shared/lib/error-utils'
import { toast } from '@/shared/lib/toast'
import type {
  ContractAddonReference,
  CreateContract as CreateContractRequest,
} from '../../services/contractService'
import { contractService } from '../../services/contractService'
import type { Customer } from '../../services/customerService'
// Service imports
import { customerService } from '../../services/customerService'
import type { EditableCharge } from '../../types/customerDetail.types'
import { AddonDialog } from './AddonDialog'
import { AddonsSection } from './AddonsSection'
import { AgentPricingDrawer } from './AgentPricingDrawer'
import { ChargesSection } from './ChargesSection'
// Component imports
import { ContractPreviewCard } from './ContractPreviewCard'
import { DiscountDialog } from './DiscountDialog'
import { DiscountsSection } from './DiscountsSection'
import { SubscriptionDetails } from './SubscriptionDetails'

interface CreateContractProps {
  customerId: string
}

// Transform tiers from various frontend formats to backend expected format
function transformTiers(
  tiers: Record<string, unknown>[]
): Array<{ min_units: number; max_units: number | null; unit_price: string }> {
  return tiers
    .filter(tier => tier && typeof tier === 'object')
    .map(tier => {
      // Handle all possible tier format variations
      let min_units = 0
      let max_units: number | null = null
      let unit_price = '0'

      // Extract min_units (firstUnit, minUnits, or min_units)
      if (typeof tier.firstUnit === 'number') {
        min_units = tier.firstUnit
      } else if (typeof tier.minUnits === 'number') {
        min_units = tier.minUnits
      } else if (typeof tier.min_units === 'number') {
        min_units = tier.min_units
      }

      // Extract max_units (lastUnit, maxUnits, or max_units)
      if (typeof tier.lastUnit === 'number') {
        max_units = tier.lastUnit === 999999999 ? null : tier.lastUnit
      } else if (typeof tier.maxUnits === 'number') {
        max_units = tier.maxUnits === 999999999 ? null : tier.maxUnits
      } else if (typeof tier.max_units === 'number') {
        max_units = tier.max_units === 999999999 ? null : tier.max_units
      } else if (
        tier.lastUnit === null ||
        tier.maxUnits === null ||
        tier.max_units === null
      ) {
        max_units = null
      }

      // Extract unit_price (perUnitPrice, unitPrice, or unit_price)
      if (tier.perUnitPrice !== undefined) {
        unit_price = String(tier.perUnitPrice)
      } else if (tier.unitPrice !== undefined) {
        unit_price = String(tier.unitPrice)
      } else if (tier.unit_price !== undefined) {
        unit_price = String(tier.unit_price)
      }

      return {
        min_units,
        max_units,
        unit_price,
      }
    })
    .filter(
      tier =>
        // Only include tiers with valid data
        typeof tier.min_units === 'number' &&
        (typeof tier.max_units === 'number' || tier.max_units === null) &&
        tier.unit_price.length > 0
    )
}

interface CouponDisplayInfo {
  id: string
  name: string
  code: string
  discount: number
  type: 'percentage' | 'fixed_amount'
  description?: string
  isValid: boolean
  validityInfo?: string
}

// Tax rates will be fetched dynamically from the API via TaxDialog

// Normalize API tiers (start_unit/end_unit/price_per_unit) to frontend format (firstUnit/lastUnit/perUnitPrice)
function normalizeTiers(
  tiers: Record<string, unknown>[] | undefined | null
): Record<string, unknown>[] | null {
  if (!tiers || tiers.length === 0) return null
  return tiers.map(tier => ({
    firstUnit: tier.start_unit ?? tier.firstUnit ?? tier.min_units ?? 0,
    lastUnit: tier.end_unit ?? tier.lastUnit ?? tier.max_units ?? null,
    perUnitPrice:
      tier.price_per_unit ?? tier.perUnitPrice ?? tier.unit_price ?? 0,
  }))
}

export function CreateContract({ customerId }: CreateContractProps) {
  const navigate = useNavigate()
  const { setBreadcrumbs } = useBreadcrumbs()

  // Load available plans using React Query
  const { data: plansResult } = usePlans({ category: 'plan', status: 'active' })
  const availablePlans = plansResult?.data || []

  // Core state
  const [customer, setCustomer] = useState<Customer | null>(null)
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null)
  const [editableCharges, setEditableCharges] = useState<EditableCharge[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Form state
  const [selectedPlanId, setSelectedPlanId] = useState<string>('')
  const [startDate, setStartDate] = useState<Date | undefined>(new Date())
  const [endDate, setEndDate] = useState<Date | undefined>(undefined)
  const [isForever, setIsForever] = useState(true)
  const [currency, setCurrency] = useState('USD')

  // Additional contract features
  const [selectedAddonIds, setSelectedAddonIds] = useState<
    ContractAddonReference[]
  >([])
  const [discounts, setDiscounts] = useState<CouponDisplayInfo[]>([])
  const [taxes, setTaxes] = useState<Array<Record<string, unknown>>>([])

  // Dialog states
  const [showAddonDialog, setShowAddonDialog] = useState(false)
  const [showDiscountDialog, setShowDiscountDialog] = useState(false)
  const [showTaxDialog, setShowTaxDialog] = useState(false)
  const [activeChargeTab, setActiveChargeTab] = useState('fixed')
  const [showAgentPricingDrawer, setShowAgentPricingDrawer] = useState(false)
  const [selectedAgentForPricing, setSelectedAgentForPricing] =
    useState<EditableCharge | null>(null)

  // Load customer
  useEffect(() => {
    const loadData = async () => {
      setLoading(true)
      try {
        // Load customer
        const foundCustomer = await customerService.getCustomerById(customerId)
        if (foundCustomer) {
          setCustomer(foundCustomer)
        }
      } catch (err) {
        setError('Failed to load data')
        toast.error(extractErrorMessage(err))
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [customerId])

  // Load plan details when plan is selected
  useEffect(() => {
    const loadPlanDetails = async () => {
      if (!selectedPlanId) {
        setSelectedPlan(null)
        setEditableCharges([])
        return
      }

      try {
        const planDetails = await planService.getPlan(selectedPlanId)
        if (planDetails) {
          setSelectedPlan(planDetails as unknown as Plan)
          setCurrency(planDetails.currency)

          // Convert plan charges, features and agents to editable charges
          const charges: EditableCharge[] = []

          // Add plan charges (fixed and recurring costs)
          // PlanCharge fields: charge_name, charge_code, charge_type, amount, billing_frequency
          planDetails.charges?.forEach(charge => {
            charges.push({
              id: charge.id,
              category:
                charge.charge_type === 'one_time' ? 'fixed' : 'recurring',
              type: charge.charge_type === 'one_time' ? 'setup' : 'platform',
              name: charge.charge_name,
              description: charge.description || '',
              unitPrice:
                typeof charge.amount === 'string'
                  ? parseFloat(charge.amount)
                  : charge.amount || 0,
              originalPrice:
                typeof charge.amount === 'string'
                  ? parseFloat(charge.amount)
                  : charge.amount || 0,
              isEdited: false,
              billingTiming: charge.billing_frequency ? 'arrears' : 'advance',
              includedUnits: 0,
              pricingModel: 'flat',
              minCommitment: 0,
            })
          })

          // Add feature charges
          // PlanFeature fields: feature_id, feature?.feature_name, billable_signal?.signal_name,
          // billable_signal_id, pricing_model, unit_price, included_units, minimum_commitment, pricing_tiers
          planDetails.features?.forEach(feature => {
            const featureName =
              feature.feature?.feature_name ||
              feature.billable_signal?.signal_name ||
              'Feature'
            charges.push({
              id: feature.feature_id,
              category: 'feature',
              type: 'usage',
              name: featureName,
              description: feature.billable_signal?.signal_name || featureName,
              unitPrice:
                feature.pricing_model === 'package' && feature.package_price
                  ? feature.package_price
                  : feature.unit_price || 0,
              originalPrice:
                feature.pricing_model === 'package' && feature.package_price
                  ? feature.package_price
                  : feature.unit_price || 0,
              isEdited: false,
              billingTiming: 'arrears',
              includedUnits: feature.included_units || 0,
              pricingModel:
                (feature.pricing_model as EditableCharge['pricingModel']) ||
                'flat',
              minCommitment: feature.minimum_commitment || 0,
              packageSize: feature.package_size ?? null,
              packagePrice: feature.package_price ?? null,
              signalId: feature.billable_signal_id || undefined,
              signalName: feature.billable_signal?.signal_name,
              tiers: normalizeTiers(
                feature.pricing_tiers
              ) as EditableCharge['tiers'],
            })
          })

          // Add agent charges
          // PlanResponse has agent_actions (flat array), group by agent
          // PlanAgentAction fields: agent?.id, agent?.name, action?.action_name, action?.action_type,
          // billable_signal?.signal_name, billable_signal_id, pricing_model, unit_price, pricing_tiers
          const agentActions = planDetails.agent_actions || []
          // Group by agent ID
          const agentMap = new Map<
            string,
            {
              agentId: string
              agentName: string
              actions: typeof agentActions
            }
          >()
          for (const action of agentActions) {
            const agentId = action.agent?.id || 'unknown'
            const agentName = action.agent?.name || 'Unknown Agent'
            if (!agentMap.has(agentId)) {
              agentMap.set(agentId, {
                agentId,
                agentName,
                actions: [],
              })
            }
            agentMap.get(agentId)!.actions.push(action)
          }

          for (const [, agent] of agentMap) {
            for (const action of agent.actions) {
              const signalName =
                action.billable_signal?.signal_name ||
                action.action?.action_name ||
                'Action'

              charges.push({
                id: `${agent.agentId}-${action.billable_signal_id}`,
                category: 'agent',
                type: 'usage',
                name: signalName,
                description: action.action?.action_name || '',
                unitPrice:
                  action.pricing_model === 'package' && action.package_price
                    ? action.package_price
                    : action.unit_price || 0,
                originalPrice:
                  action.pricing_model === 'package' && action.package_price
                    ? action.package_price
                    : action.unit_price || 0,
                isEdited: false,
                billingTiming: 'arrears',
                includedUnits: action.included_units || 0,
                pricingModel:
                  (action.pricing_model as EditableCharge['pricingModel']) ||
                  'flat',
                minCommitment: action.minimum_commitment || 0,
                packageSize: action.package_size ?? null,
                packagePrice: action.package_price ?? null,
                agentId: agent.agentId,
                agentName: agent.agentName,
                signalId: action.billable_signal_id,
                signalName: signalName,
                kind: (action.action?.action_type ||
                  'task') as EditableCharge['kind'],
                tiers: normalizeTiers(
                  action.pricing_tiers
                ) as EditableCharge['tiers'],
                billingPeriod: 'monthly',
              })
            }
          }

          setEditableCharges(charges)
        }
      } catch (err) {
        setError('Failed to load plan details')
        toast.error(extractErrorMessage(err))
      }
    }

    loadPlanDetails()
  }, [selectedPlanId])

  // Breadcrumbs setup
  const breadcrumbsContent = useMemo(() => {
    if (!customer) return null

    return (
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink to='/dashboard' className='flex items-center gap-1'>
              <Home className='w-4 h-4' />
              Dashboard
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink to='/customers'>Customers</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink to={`/customers/${customerId}`}>
              {customer.name}
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink to={`/customers/${customerId}?tab=contracts`}>
              Contracts
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>Create Contract</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
    )
  }, [customer, customerId])

  useEffect(() => {
    if (breadcrumbsContent) {
      setBreadcrumbs(breadcrumbsContent)
    }
  }, [setBreadcrumbs, breadcrumbsContent])

  // Event handlers
  const handleChargeUpdate = (chargeId: string, newPrice: number) => {
    setEditableCharges(prev =>
      prev.map(charge =>
        charge.id === chargeId
          ? {
              ...charge,
              unitPrice: newPrice,
              isEdited: newPrice !== charge.originalPrice,
            }
          : charge
      )
    )
  }

  const handleEditCharge = (charge: EditableCharge) => {
    setSelectedAgentForPricing(charge)
    setShowAgentPricingDrawer(true)
  }

  const handleUpdateCharge = (updatedCharge: EditableCharge) => {
    setEditableCharges(prev =>
      prev.map(charge =>
        charge.id === updatedCharge.id ? updatedCharge : charge
      )
    )
  }

  const handleDeleteCharge = (chargeId: string) => {
    setEditableCharges(prev => prev.filter(charge => charge.id !== chargeId))
  }

  const handleAddAddon = (addonPlanId: string) => {
    setSelectedAddonIds(prev => [
      ...prev,
      {
        plan_id: addonPlanId,
      },
    ])
  }

  const handleRemoveAddon = (addonPlanId: string) => {
    setSelectedAddonIds(prev => prev.filter(a => a.plan_id !== addonPlanId))
  }

  const handleAddCoupon = (coupon: CouponDisplayInfo) => {
    setDiscounts(prev => [...prev, coupon])
  }

  const handleRemoveCoupon = (couponId: string) => {
    setDiscounts(prev => prev.filter(d => d.id !== couponId))
  }

  const handleAddTaxRate = (
    taxRate: Record<string, unknown>,
    currency: string
  ) => {
    setTaxes(prev => [
      ...prev,
      { ...taxRate, currency, autoApply: true, priority: 1 },
    ])
  }

  const handleRemoveTaxRate = (taxId: string) => {
    setTaxes(prev => prev.filter(t => t.id !== taxId))
  }

  const handleSubmit = async () => {
    if (!selectedPlan || !customer || !startDate) return

    setSaving(true)
    setError(null)

    try {
      // Convert Date objects to YYYY-MM-DD format for API
      const formattedStartDate = startDate.toISOString().split('T')[0]
      const formattedEndDate =
        endDate && !isForever ? endDate.toISOString().split('T')[0] : undefined

      // Map billing period from plan to contract billing interval
      const billingInterval = selectedPlan.billing_period || 'monthly'

      // Build ALL line items with proper categorization
      const line_item_overrides = editableCharges.map(charge => {
        // Determine line item type and related fields
        let line_item_type:
          | 'setup'
          | 'recurring'
          | 'usage_agent'
          | 'usage_feature'
        let agent_id: string | undefined
        let signal_id: string | undefined
        let feature_id: string | undefined
        let billing_cycle: 'monthly' | 'quarterly' | 'yearly' | undefined

        if (charge.category === 'fixed') {
          // One-time setup fees
          line_item_type = 'setup'
          billing_cycle = undefined
        } else if (charge.category === 'recurring') {
          // Recurring platform fees
          line_item_type = 'recurring'
          billing_cycle = billingInterval as 'monthly' | 'quarterly' | 'yearly'
        } else if (
          charge.category === 'agent' &&
          charge.agentId &&
          charge.signalId
        ) {
          // Agent-based usage
          line_item_type = 'usage_agent'
          agent_id = charge.agentId
          signal_id = charge.signalId
          billing_cycle = undefined // Usage-based billing
        } else if (charge.category === 'feature') {
          // Feature-based usage
          line_item_type = 'usage_feature'
          feature_id = charge.id // This is the feature_id from the charges
          signal_id = charge.signalId?.trim() ? charge.signalId : undefined // Handle empty string
          billing_cycle = undefined // Usage-based billing
        } else {
          // Fallback for unknown categories
          line_item_type = 'recurring'
          billing_cycle = billingInterval as 'monthly' | 'quarterly' | 'yearly'
        }

        const result: Record<string, unknown> = {
          line_item_type,
          name: charge.name,
          description: charge.description || undefined,
          unit_price: Number(charge.unitPrice),
          pricing_model: charge.pricingModel || PRICING_MODEL.flat,
          included_units: charge.includedUnits || 0,
          minimum_commitment: charge.minCommitment || 0,
          currency: currency,
        }

        // Only add optional fields if they have valid values
        if (agent_id) result.agent_id = agent_id
        if (signal_id) result.signal_id = signal_id // Only add if it's a non-empty string
        if (feature_id) result.feature_id = feature_id
        if (billing_cycle) result.billing_cycle = billing_cycle
        if (charge.tiers && charge.tiers.length > 0) {
          result.tiers = transformTiers(
            charge.tiers as unknown as Record<string, unknown>[]
          )
        }

        return result
      })

      // Use new simplified addon format - just pass the addon plan IDs
      // Backend will fetch addon details and apply current pricing

      // Transform taxes to API format
      const apiTaxes = taxes.map(tax => ({
        tax_id: tax.id,
        priority: tax.priority || 1,
      }))

      // Transform discounts to API format - use coupon_id for referencing existing coupons
      const apiDiscounts = discounts.map(discount => ({
        coupon_id: discount.id,
        applies_to: 'all' as const,
        priority: 1,
        stacking_behavior: 'additive' as const,
      }))

      const request = {
        customer_id: customer.id,
        plan_id: selectedPlan.id,
        name: `${selectedPlan.name} - ${customer.name}`,
        start_date: formattedStartDate,
        end_date: formattedEndDate,
        currency,
        billing_interval: billingInterval,
        line_item_overrides: line_item_overrides,
        addon_plan_ids:
          selectedAddonIds.length > 0 ? selectedAddonIds : undefined,
        taxes: apiTaxes.length > 0 ? apiTaxes : undefined,
        discounts: apiDiscounts.length > 0 ? apiDiscounts : undefined,
      } as CreateContractRequest

      await contractService.createContract(request)

      // Navigate back to customer contracts tab
      navigate({
        to: '/customers/$customerId',
        params: { customerId },
        search: { tab: 'contracts' },
      })
    } catch (err) {
      setError('Failed to create contract')
      toast.error(extractErrorMessage(err))
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    navigate({
      to: '/customers/$customerId',
      params: { customerId },
      search: { tab: 'contracts' },
    })
  }

  if (loading) {
    return (
      <div className='flex items-center justify-center py-12'>
        <div className='text-muted-foreground'>Loading...</div>
      </div>
    )
  }

  if (!customer) {
    return (
      <div className='text-center py-12'>
        <div className='text-red-600'>Customer not found</div>
      </div>
    )
  }

  return (
    <div className='min-h-screen'>
      <div className='max-w-[1600px] mx-auto px-8  pb-8'>
        {/* Header */}
        <div className='mb-8'>
          <PageTitle
            title={customer?.name || 'Customer'}
            subtitle={`Create a new contract for ${customer?.name || 'Customer'}`}
          />
        </div>

        {error && (
          <div className='bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-6'>
            {error}
          </div>
        )}

        <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
          {/* Main Content */}
          <div className='lg:col-span-2 space-y-6'>
            {/* Subscription Details */}
            <SubscriptionDetails
              availablePlans={
                availablePlans as unknown as import('../../services/plansService').Plan[]
              }
              selectedPlanId={selectedPlanId}
              setSelectedPlanId={setSelectedPlanId}
              selectedPlan={
                selectedPlan as unknown as
                  | import('../../services/plansService').Plan
                  | null
              }
              currency={currency}
              setCurrency={setCurrency}
              startDate={startDate}
              setStartDate={setStartDate}
              endDate={endDate}
              setEndDate={setEndDate}
              isForever={isForever}
              setIsForever={setIsForever}
            />

            {/* Plan Charges */}
            <ChargesSection
              selectedPlan={
                selectedPlan as unknown as
                  | import('../../services/plansService').Plan
                  | null
              }
              editableCharges={editableCharges}
              activeChargeTab={activeChargeTab}
              setActiveChargeTab={setActiveChargeTab}
              onChargeUpdate={handleChargeUpdate}
              onEditCharge={handleEditCharge}
            />

            {/* Addons */}
            <AddonsSection
              mode='create'
              addons={selectedAddonIds}
              onAddAddon={() => setShowAddonDialog(true)}
              onRemoveAddon={handleRemoveAddon}
            />

            {/* Taxes */}
            <TaxesSection
              taxes={taxes as never}
              onAddTax={() => setShowTaxDialog(true)}
              onRemoveTax={handleRemoveTaxRate}
            />

            {/* Discounts */}
            <DiscountsSection
              discounts={discounts.map(d => ({
                id: d.id,
                name: d.name,
                discount: d.discount,
                type: d.type === 'fixed_amount' ? 'fixed_amount' : 'percentage',
                description: d.description || '',
              }))}
              onAddDiscount={() => setShowDiscountDialog(true)}
              onRemoveDiscount={handleRemoveCoupon}
            />
          </div>

          {/* Right Preview Card */}
          <div className='space-y-6'>
            <div className='sticky top-6'>
              <ContractPreviewCard
                customer={customer}
                selectedPlan={selectedPlan}
                startDate={startDate}
                endDate={endDate}
                isForever={isForever}
                currency={currency}
                editableCharges={editableCharges}
                addons={
                  selectedAddonIds as unknown as Array<{
                    plan_id: string
                    name?: string
                    quantity?: number
                  }>
                }
                discounts={
                  discounts as unknown as Array<{
                    name: string
                    type: 'percentage' | 'fixed_amount'
                    percentage_off?: number
                    amount_off?: number
                    discount?: number
                    amount?: number
                  }>
                }
                taxes={
                  taxes as unknown as import('../../services/contractService').ContractTaxResponse[]
                }
              />

              {/* Action Buttons */}
              <div className='mt-6 space-y-3'>
                <Button
                  onClick={handleSubmit}
                  disabled={!selectedPlan || !startDate || saving}
                  className='w-full'
                  size='lg'
                >
                  {saving ? 'Creating...' : 'Create Contract'}
                </Button>
                <Button
                  variant='outline'
                  onClick={handleCancel}
                  disabled={saving}
                  className='w-full'
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Dialogs and Drawers */}
      <AgentPricingDrawer
        open={showAgentPricingDrawer}
        onOpenChange={setShowAgentPricingDrawer}
        selectedCharge={selectedAgentForPricing}
        onUpdateCharge={handleUpdateCharge}
        onDeleteCharge={handleDeleteCharge}
      />

      <AddonDialog
        open={showAddonDialog}
        onOpenChange={setShowAddonDialog}
        contractId={undefined}
        existingAddonIds={selectedAddonIds.map(addon => addon.plan_id)}
        onAddAddon={handleAddAddon}
      />

      <DiscountDialog
        open={showDiscountDialog}
        onOpenChange={setShowDiscountDialog}
        existingDiscounts={discounts}
        onAddCoupon={handleAddCoupon}
      />

      <TaxDialog
        open={showTaxDialog}
        onOpenChange={setShowTaxDialog}
        existingTaxes={taxes as Array<{ id: string; [key: string]: unknown }>}
        onAddTaxRate={handleAddTaxRate}
      />
    </div>
  )
}
